import { ChangeEvent, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z, ZodBoolean, ZodOptional, ZodString } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CarrierCodePickable,
  CarrierCodePickableType,
  ConnectionInfo,
  FieldInfo,
  useCarriersVariables,
} from "../use-carriers-variables";
import {
  CreateCarrierDto,
  UpdateCarrierDto,
} from "@babackapp/private-api-client/types";

const getZodSchema = (carrierConnectionFields: ConnectionInfo) => {
  const carrierFields = Object.entries(carrierConnectionFields);
  const credentialsFormShape: {
    [key: string]: ZodOptional<ZodString> | ZodOptional<ZodBoolean> | ZodString;
  } = {};

  carrierFields.forEach(([key, field]: [string, FieldInfo]) => {
    credentialsFormShape[key] =
      field.type === "boolean"
        ? z.boolean().optional()
        : field.required
          ? z.string().min(1, { message: `${field.label} is required` })
          : z.string().optional();
  });

  return z.object(credentialsFormShape);
};

type CarrierTransportZFormValues = z.infer<ReturnType<typeof getZodSchema>>;

export const useCarrierTransportForm = ({
  defaultValues,
  onSubmit,
}: {
  defaultValues?: {
    code: CarrierCodePickableType;
    credentials: { [key: string]: string };
  };
  onSubmit: (data: CreateCarrierDto | UpdateCarrierDto) => void;
}) => {
  type CarrierZSchema = z.ZodObject<{
    [key: string]:
      | z.ZodString
      | z.ZodOptional<z.ZodString>
      | z.ZodOptional<z.ZodBoolean>;
  }>;
  
  const [carrierCodeSelected, setCarrierCodeSelected] = useState<
    CarrierCodePickableType | undefined
  >(defaultValues ? defaultValues?.code : undefined);

  const { listCarriers } = useCarriersVariables();

  const getCarrierFormRulesByCarrierSelected = (value: CarrierCodePickable) => {
    const carrierFormFields = listCarriers.find(
      (carrier) =>
        carrier.value.toLocaleLowerCase() === value.toLocaleLowerCase(),
    )?.connectionInfo;
    if (carrierFormFields) return carrierFormFields;
  };

  const getInitCarrierTransportFormFields = useCallback(
    (carrierCode: CarrierCodePickable | undefined) => {
      return carrierCode
        ? getCarrierFormRulesByCarrierSelected(carrierCode)
        : undefined;
    },
    [],
  );

  const initDefaultCarrierTransportFormZSchema = useCallback(
    (carrierCode: CarrierCodePickable | undefined) => {
      const initZodSchema = z.object({});
      const carrierSchemaField: ConnectionInfo | undefined =
        getInitCarrierTransportFormFields(carrierCode);
      return carrierSchemaField
        ? getZodSchema(carrierSchemaField)
        : initZodSchema;
    },
    [],
  );

  const [carrierFormSchema, setCarrierFormSchema] = useState<CarrierZSchema>(
    initDefaultCarrierTransportFormZSchema(
      defaultValues?.code as CarrierCodePickable,
    ),
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset: resetForm,
    watch,
  } = useForm<CarrierTransportZFormValues>({
    resolver: zodResolver(carrierFormSchema),
    mode: "onChange",
    defaultValues: {
      ...defaultValues?.credentials,
    },
  });

  const [carrierFormFields, setCarrierFormFields] = useState<
    ConnectionInfo | undefined
  >(
    getInitCarrierTransportFormFields(
      defaultValues?.code as CarrierCodePickable,
    ),
  );

  const onChangeCarrierPicked = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const carrierCodeChanged = e.target.value as CarrierCodePickable;
      resetForm({
        code: carrierCodeChanged,
      });

      setCarrierCodeSelected(e.target.value as CarrierCodePickableType);

      if (!carrierCodeChanged) return;
      const carrierSchemaField: ConnectionInfo | undefined =
        getCarrierFormRulesByCarrierSelected(carrierCodeChanged);

      if (!carrierSchemaField) return;
      setCarrierFormFields(carrierSchemaField);
      setCarrierFormSchema(getZodSchema(carrierSchemaField));
    },
    [],
  );

  return {
    register,
    handleSubmit: handleSubmit((data) => {
      if (!carrierCodeSelected) return;
      onSubmit({
        code: carrierCodeSelected,
        credentials: { ...data },
      });
    }),
    control,
    errors,
    isValid,
    carrierFormFields,
    onChangeCarrierPicked,
    carrierCodeSelected,
  };
};
